import { ScheduleRequest } from '@weave/schema-gen-ts/dist/schemas/schedule/api/v2/api.pb';
import { ReviewedStatus } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/booking_submission.pb';
import dayjs, { Dayjs } from 'dayjs';
import { UseQueryResult } from 'react-query';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { ScheduleFFHelpers } from '@frontend/schedule-helpers';
import { getOneDayRangeFromNow } from '../../../../../../utils';
import { convertScheduleRequestV3ToV2 } from './useGetScheduleRequest';

type UseGetScheduleRequestListQueryProps =
  | {
      locationIds: string[];
    } & RequestListDateRangeParams;

export const useGetScheduleRequestListQuery = ({
  locationIds,
  ...rest
}: UseGetScheduleRequestListQueryProps): Omit<UseQueryResult<{ rows: ScheduleRequest[] }>, 'queryKey'> => {
  const { isEnabled: isScheduleRequestEventsV3Enabled } = ScheduleFFHelpers.useScheduleRequestEventsV3FeatureFlag();

  const { isScheduleV3FlagEnabled, isLoading: isLoadingFF } = SchedulerV3.Helpers.useGetSchedulerV3FeatureFlagDetails();

  const dateRangeObj = getDateRangeObj(rest);

  const scheduleRequestListQueryV3 = SchedulerV3.Queries.useListBookingSubmissionsQuery({
    request: {
      locationsIds: locationIds,
      reviewedStatuses: [ReviewedStatus.PENDING],
      ...dateRangeObj,
    },
    options: {
      enabled: !isLoadingFF && isScheduleV3FlagEnabled && isScheduleRequestEventsV3Enabled && locationIds.length > 0,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      select: (data) => ({
        rows: (data.bookingSubmissions ?? []).map<ScheduleRequest>(convertScheduleRequestV3ToV2),
      }),
    },
  });

  return scheduleRequestListQueryV3;
};

export type RequestListDateRangeParams =
  | { startDate?: Dayjs | string; endDate?: Dayjs | string; date?: never }
  | { startDate?: never; endDate?: never; date: Dayjs | string };

const getDateRangeObj = ({
  date,
  startDate,
  endDate,
}: RequestListDateRangeParams): { requestedSlotsBetween: { start: string; end: string } } | undefined => {
  let [start, end] = [
    startDate ? dayjs(String(startDate)).utc().toISOString() : undefined,
    endDate ? dayjs(String(endDate)).utc().toISOString() : undefined,
  ];
  if (date) {
    const dateRangeArr = getOneDayRangeFromNow(date);
    start = dateRangeArr[0];
    end = dateRangeArr[1];
  }
  return start && end ? { requestedSlotsBetween: { start, end } } : undefined;
};
